import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Users, 
  UserCheck,
  UserX,
  RefreshCw,
  Calendar,
  Download
} from 'lucide-react';
import api from '@/services/api';
import { format } from 'date-fns';

const AttendanceTracking = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: format(new Date(), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [stats, setStats] = useState({
    total: 0,
    present: 0,
    absent: 0
  });

  // Fetch attendance data
  const fetchAttendance = async (isManualRefresh = false) => {
    try {
      setLoading(true);

      let response;
      if (dateRange.start === dateRange.end && dateRange.start === format(new Date(), 'yyyy-MM-dd')) {
        // Fetch today's live attendance
        response = await api.get('/attendance/ngteco/today');
      } else {
        // Fetch attendance history
        response = await api.get('/attendance/ngteco/history', {
          params: {
            start_date: dateRange.start,
            end_date: dateRange.end
          }
        });
      }

      if (response.data.success) {
        const records = response.data.data.records || [];
        setAttendanceData(records);
        setLastSync(response.data.data.lastSync);
        
        // Calculate stats
        const presentCount = records.filter(r => r.status === 'Present').length;
        const absentCount = records.filter(r => r.status === 'Absent').length;
        
        setStats({
          total: records.length,
          present: presentCount,
          absent: absentCount
        });
      }
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 5 minutes
  useEffect(() => {
    fetchAttendance();
    const interval = setInterval(() => {
      fetchAttendance();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [dateRange]);

  // Manual refresh
  const handleRefresh = () => {
    fetchAttendance(true);
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Name', 'Employee ID', 'Department', 'Designation', 'Status'];
    const rows = attendanceData.map(record => [
      `${record.first_name} ${record.last_name}`,
      record.employee_code,
      record.department,
      record.designation,
      record.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${dateRange.start}_to_${dateRange.end}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Attendance Management</h1>
              <p className="text-sm text-gray-500 mt-1">Real-time attendance tracking powered by NGTeco</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-4 py-2 bg-white border text-gray-700 rounded-xl hover:bg-gray-50 transition-all text-sm font-medium flex items-center gap-2 shadow-sm"
                style={{ borderColor: '#e5e7eb' }}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <button
                onClick={exportToCSV}
                disabled={attendanceData.length === 0}
                className="px-4 py-2 bg-[#DF6B38] text-white rounded-xl hover:opacity-90 transition-all text-sm font-medium flex items-center gap-2 shadow-sm"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
            </div>
          </div>
          
          {lastSync && (
            <p className="text-xs text-gray-400 mt-3">
              Last synced: {format(new Date(lastSync), 'MMM dd, yyyy h:mm a')}
            </p>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total */}
          <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: '0.5px solid #e5e7eb' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</span>
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                <Users className="h-4 w-4 text-blue-600" strokeWidth={2} />
              </div>
            </div>
            <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-400 mt-1">Employees tracked</p>
          </div>

          {/* Present */}
          <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: '0.5px solid #e5e7eb' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Present</span>
              <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center">
                <UserCheck className="h-4 w-4 text-green-600" strokeWidth={2} />
              </div>
            </div>
            <p className="text-2xl font-semibold text-green-600">{stats.present}</p>
            <p className="text-xs text-gray-400 mt-1">
              {stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0}% attendance rate
            </p>
          </div>

          {/* Absent */}
          <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: '0.5px solid #e5e7eb' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Absent</span>
              <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center">
                <UserX className="h-4 w-4 text-red-600" strokeWidth={2} />
              </div>
            </div>
            <p className="text-2xl font-semibold text-red-600">{stats.absent}</p>
            <p className="text-xs text-gray-400 mt-1">Employees not present</p>
          </div>
        </div>

        {/* Date Picker */}
        <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="flex items-center gap-4">
            <Calendar className="h-5 w-5 text-gray-400" />
            <div className="flex items-center gap-3 flex-1">
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="px-3 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-[#DF6B38] focus:border-transparent"
                  style={{ borderColor: '#e5e7eb' }}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="px-3 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-[#DF6B38] focus:border-transparent"
                  style={{ borderColor: '#e5e7eb' }}
                />
              </div>
              <button
                onClick={() => setDateRange({ start: format(new Date(), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') })}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all text-sm font-medium mt-5"
              >
                Today
              </button>
            </div>
          </div>
        </div>

        {/* Attendance Table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="p-5 border-b" style={{ borderColor: '#e5e7eb' }}>
            <h2 className="text-lg font-semibold text-gray-900">Attendance Records</h2>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center p-12">
                <RefreshCw className="h-8 w-8 text-[#DF6B38] animate-spin" />
              </div>
            ) : attendanceData.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No attendance records found</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Employee ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Designation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendanceData.map((record, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {record.first_name} {record.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{record.employee_code}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{record.department}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{record.designation}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          record.status === 'Present' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {record.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceTracking;
